import { ref, type Ref } from "vue";

/**
 * Очередь отложенного сохранения на сервер.
 *
 * Правки в настройках идут пачками (несколько ячеек подряд), а апстрим режет
 * частоту запросов. Поэтому в очереди всегда лежит только ПОСЛЕДНЕЕ состояние:
 * новая правка заменяет ожидающую, запрос уходит после паузы, и никогда не идут два
 * запроса одновременно — правка, пришедшая во время отправки, уйдёт следующим
 * запросом после её завершения. Сбой не теряет данные: состояние остаётся в очереди,
 * повторы идут по нарастающим паузам, а следующая правка или `flush()` пробуют снова.
 */

export type RemoteSaveState = "idle" | "pending" | "saving" | "error";

export interface RemoteSaveQueueOptions<T> {
  save: (payload: T) => Promise<void>;
  /** Пауза после последней правки до отправки. */
  debounceMs?: number;
  /** Паузы перед повторами после сбоя; после последней очередь ждёт следующего толчка. */
  retryDelaysMs?: number[];
  onError?: (error: unknown) => void;
}

export interface RemoteSaveQueue<T> {
  readonly state: Ref<RemoteSaveState>;
  /** Заменяет ожидающее состояние и перезапускает паузу. */
  schedule: (payload: T) => void;
  /** Отправляет ожидающее состояние сейчас, дожидается результата (ошибку не бросает). */
  flush: () => Promise<void>;
  /** Что ещё не доехало до сервера — для отправки при закрытии вкладки. */
  pendingPayload: () => T | null;
  /** Останавливает таймеры; ожидающее состояние остаётся доступным через pendingPayload. */
  dispose: () => void;
}

const DEFAULT_DEBOUNCE_MS = 800;
const DEFAULT_RETRY_DELAYS_MS = [2_000, 5_000, 15_000];

export function createRemoteSaveQueue<T>(options: RemoteSaveQueueOptions<T>): RemoteSaveQueue<T> {
  const debounceMs = options.debounceMs ?? DEFAULT_DEBOUNCE_MS;
  const retryDelays = options.retryDelaysMs ?? DEFAULT_RETRY_DELAYS_MS;

  const state = ref<RemoteSaveState>("idle");
  let pending: T | null = null;
  let timer: ReturnType<typeof setTimeout> | null = null;
  let inflight: Promise<void> | null = null;
  let failures = 0;
  let disposed = false;

  function clearTimer(): void {
    if (timer === null) return;
    clearTimeout(timer);
    timer = null;
  }

  function armTimer(delay: number): void {
    clearTimer();
    timer = setTimeout(() => {
      timer = null;
      run();
    }, delay);
  }

  function onSaved(): void {
    failures = 0;
    if (pending === null) {
      state.value = "idle";
      return;
    }
    // Правка пришла во время отправки — уходит сразу следующим запросом
    run();
  }

  function onFailed(payload: T, error: unknown): void {
    // Более свежая правка уже заменила упавшую — она и поедет
    pending ??= payload;
    state.value = "error";
    options.onError?.(error);

    const delay = retryDelays[failures];
    failures += 1;
    if (delay !== undefined && !disposed) armTimer(delay);
  }

  function run(): Promise<void> {
    if (inflight) return inflight;
    if (pending === null || disposed) return Promise.resolve();

    const payload = pending;
    pending = null;
    state.value = "saving";

    // Исход разбирается уже после того, как запрос перестал быть текущим: иначе
    // следующая отправка из onSaved увидела бы «запрос ещё идёт» и не состоялась
    inflight = options.save(payload).then(
      () => {
        inflight = null;
        onSaved();
      },
      (error: unknown) => {
        inflight = null;
        onFailed(payload, error);
      },
    );

    return inflight;
  }

  function schedule(payload: T): void {
    if (disposed) return;
    pending = payload;
    if (state.value !== "saving") state.value = "pending";
    armTimer(debounceMs);
  }

  async function flush(): Promise<void> {
    clearTimer();
    await (inflight ?? Promise.resolve());
    await run();
  }

  return {
    state,
    schedule,
    flush,
    pendingPayload: () => pending,
    dispose: () => {
      disposed = true;
      clearTimer();
    },
  };
}
